import React, { useMemo, useEffect, useCallback, useState } from 'react';
import { Loader2, RefreshCw, AlertCircle } from 'lucide-react';
import { GameErrorBoundary } from '../components/GameErrorBoundary';
import {
  GameHeader,
  GameMainContent,
  GameControls,
  GameFooter
} from '../components/game';
import StockChart from '../components/game/StockChart';
import FinancialMetrics from '../components/game/FinancialMetrics';
import { WsbSlotMachineBackground } from '../components/game/WsbSlotMachineBackground';
import { GuessFeedbackOverlay } from '../components/game/GuessFeedbackOverlay';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { DesktopGameLayout } from '../components/layout/DesktopGameLayout';
import { MobileGameLayout } from '../components/layout/MobileGameLayout';
import { FinishedModal } from '../components/ui/modals/FinishedModal';
import { BaronWuffettDialogue } from '../components/avatar/BaronWuffettDialogue';
import { AnalysisSpotlight } from '../components/avatar/AnalysisSpotlight';

import { TutorialOverlay } from '../components/tutorial/TutorialOverlay';
import { MobileTutorialOverlay } from '../components/tutorial/MobileTutorialOverlay';
import { useTutorial } from '../contexts/TutorialContext';
import { DevResetButton } from '../components/DevResetButton';
import { playCorrectSound, playIncorrectSound } from '../utils/sound';

// Hooks
import {
  useGameLogic,
  useKeyboardHandler,
  useGameEffects
} from '../hooks';
import { useSubmitPlay } from '../hooks/useSubmitPlay';
import { useAuth } from '../hooks/useAuth';
import { useCategoryStockData } from '../hooks/useCategoryStockData';
import { useCategoryStockOptions } from '../hooks/useCategoryStockOptions';
import { useHints } from '../hooks/useHints';
import { useLayoutVariant } from '../hooks/useLayoutVariant';
import type { UseCategoryBootstrapReturn } from '../hooks/useCategoryBootstrap';
import { useFinancialMetrics } from '../hooks/useFinancialMetrics';
import { useCategoryGameActions, useCategoryGameState } from '../contexts/GameContext';
import { useGuessHistorySync } from '../hooks/useGuessHistorySync';
import { useAnalysis } from '../hooks/useAnalysis';
import { usePremium } from '../hooks/usePremium';
import { getCurrentGameTimestamp } from '../config/dateTimeConfig';
import { supabase } from '../config/supabase';

// Types
import { HintData, GuessResult } from '../types/game';
import { StockCategoryFilter, CategoryDisplayConfig } from '../types/stockCategories';
import { cn } from '../utils/cn';
import { getOrCreateDeviceId } from '../utils/deviceId';

interface GamePageProps {
  className?: string;
  category?: StockCategoryFilter;
  categoryConfig?: CategoryDisplayConfig;
  bootstrap?: UseCategoryBootstrapReturn;
  isReplayMode?: boolean;
  gameDate?: string;
}

// Loading Component
const GameLoading: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <Loader2 className="mx-auto mb-4 animate-spin text-primary" size={48} />
      <h2 className="text-lg font-semibold mb-2">Loading today's stock...</h2>
      <p className="text-gray-600 dark:text-gray-400">
        Preparing your daily challenge
      </p>
    </div>
  </div>
);

// Error Display Component
interface GameErrorProps {
  error: string;
  onRetry: () => void;
}

const GameError: React.FC<GameErrorProps> = ({ error, onRetry }) => (
  <div className="min-h-screen flex items-center justify-center p-4">
    <Card className="max-w-md w-full p-6 text-center">
      <AlertCircle className="mx-auto mb-4 text-red-500" size={48} />
      <h2 className="text-lg font-semibold mb-2">Unable to load game</h2>
      <p className="text-gray-600 dark:text-gray-400 mb-4">
        {error}
      </p>
      <Button onClick={onRetry} className="w-full">
        <RefreshCw className="w-4 h-4 mr-2" />
        Try Again
      </Button>
    </Card>
  </div>
);

export const GamePage: React.FC<GamePageProps> = ({
  className,
  category,
  categoryConfig,
  bootstrap,
  isReplayMode = false,
  gameDate: replayDate
}) => {
  const hasBootstrapData = Boolean(bootstrap);

  // Layout detection for conditional rendering - now properly memoized in the hook
  const layoutVariant = useLayoutVariant();
  const shouldCenterStreamerLayout = layoutVariant.isStreamerMode;

  // Tutorial management - start tutorial on first visit (desktop or mobile)
  const { tutorialState, startTutorial, skipTutorial } = useTutorial();

  // Ref for auto-scrolling to financial metrics on mobile (wrong guess)
  const financialMetricsRef = React.useRef<HTMLElement>(null);

  // Dev-only: Get reset function for testing/recording
  const { devResetCategory, setMarketCapLookup } = useCategoryGameActions();

  // Get devMode from context (for dev test page)
  const { devMode } = useCategoryGameState();

  useEffect(() => {
    if (tutorialState.hasCompleted || tutorialState.isActive) return;

    if (layoutVariant.type === 'desktop') {
      // Desktop: auto-skip tutorial so promo modals can trigger immediately
      skipTutorial();
    } else {
      // Mobile: auto-start tutorial after a short delay
      const timer = setTimeout(() => {
        startTutorial();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [layoutVariant.type, tutorialState.hasCompleted, tutorialState.isActive, startTutorial, skipTutorial]);

  // Fetch stock options for autocomplete (category-specific) - moved earlier for useGameLogic
  const {
    options: stockOptions,
    marketCaps: stockMarketCaps,
    loading: stockOptionsLoading,
    error: stockOptionsError,
    searchOptions: searchStockOptions
  } = useCategoryStockOptions(category);

  // Push market caps into game context for direction badge computation
  useEffect(() => {
    if (stockMarketCaps && Object.keys(stockMarketCaps).length > 0) {
      setMarketCapLookup(stockMarketCaps);
    }
  }, [stockMarketCaps, setMarketCapLookup]);

  // Game logic and state management
  const {
    gameState,
    currentCategory,
    isShaking,
    isSubmitting,
    handleGuessSubmit,
    handleShare,
    handleRetry,
    handleErrorBoundaryReset,
    generateGameResult,
    triggerHapticFeedback,
    updateGameState,
    updateStockData,
    setGameResult,
  } = useGameLogic({ stockOptions });

  // Normalize category once and use it everywhere to prevent prop thrashing
  const activeCategory = useMemo<StockCategoryFilter>(
    () => (category ?? currentCategory) as StockCategoryFilter,
    [category, currentCategory]
  );

  // Memoize retry handler to prevent recreation on every render
  const memoizedHandleRetry = useCallback(() => {
    handleRetry();
  }, [handleRetry]);

  // Get/set modal dismissal state from localStorage
  const getModalDismissalKey = useCallback(() => {
    return `wallstreetle_modalDismissed_${activeCategory}`;
  }, [activeCategory]);

  const isModalDismissedForToday = useCallback(() => {
    const key = getModalDismissalKey();
    const dismissalData = localStorage.getItem(key);
    if (!dismissalData) return false;

    try {
      const { date } = JSON.parse(dismissalData);
      const currentGameDate = gameState.gameDate || new Date().toISOString().split('T')[0];
      return date === currentGameDate;
    } catch {
      return false;
    }
  }, [getModalDismissalKey, gameState.gameDate]);

  const setModalDismissed = useCallback(() => {
    const key = getModalDismissalKey();
    const currentGameDate = gameState.gameDate || new Date().toISOString().split('T')[0];
    localStorage.setItem(key, JSON.stringify({ date: currentGameDate }));
  }, [getModalDismissalKey, gameState.gameDate]);

  const [isFinishedModalOpen, setIsFinishedModalOpen] = useState(false);
  const [showBaronDialogue, setShowBaronDialogue] = useState(false);
  const [progressRefreshKey, setProgressRefreshKey] = useState(0);
  const isFirstFinishedOpenRef = React.useRef(true);
  const [spotlightStep, setSpotlightStep] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [feedbackType, setFeedbackType] = useState<'correct' | 'incorrect' | null>(null);
  const prevGuessCountRef = React.useRef(gameState.guesses.length);

  // Memoize modal close handler to prevent recreation
  const handleModalClose = useCallback(() => {
    setIsFinishedModalOpen(false);
    setModalDismissed();
  }, [setModalDismissed]);

  // Memoize finished modal state handler to prevent unnecessary re-runs
  const handleFinishedModalState = useCallback(() => {
    if (gameState.gameStatus === 'playing') {
      setIsFinishedModalOpen(false);
      isFirstFinishedOpenRef.current = true; // Reset for next game completion
      return;
    }

    // Only auto-open if not dismissed for today's game
    if (!isModalDismissedForToday()) {
      setIsFinishedModalOpen(true);
    }
  }, [gameState.gameStatus, isModalDismissedForToday]);

  useEffect(() => {
    handleFinishedModalState();
  }, [gameState.gameStatus, handleFinishedModalState]); // Keep callback dependency for correctness

  const handleInvalidGuess = useCallback((error: string) => {
    setValidationError(error);
    triggerHapticFeedback('heavy');
  }, [triggerHapticFeedback]);

  const handleClearValidationError = useCallback(() => {
    setValidationError(null);
  }, []);

  // Trigger guess feedback overlay when a new guess is added
  useEffect(() => {
    const currentCount = gameState.guesses.length;
    if (currentCount > prevGuessCountRef.current && currentCount > 0) {
      const latestGuess = gameState.guesses[currentCount - 1];
      setFeedbackType(latestGuess.isCorrect ? 'correct' : 'incorrect');
      if (latestGuess.isCorrect) {
        playCorrectSound();
      } else {
        playIncorrectSound();
      }
      const timer = setTimeout(() => setFeedbackType(null), 3000);
      return () => clearTimeout(timer);
    }
    prevGuessCountRef.current = currentCount;
  }, [gameState.guesses]);

  const handleOpenResults = useCallback(() => {
    isFirstFinishedOpenRef.current = false; // Not the first open — user clicked "Results"
    setIsFinishedModalOpen(true);
  }, []);

  // Persistent ref to track whether click was recorded this session
  const analysisClickRecordedRef = React.useRef(false);

  // Reset click-recorded flag when category or game date changes
  useEffect(() => {
    analysisClickRecordedRef.current = false;
  }, [activeCategory, gameState.gameDate]);

  const recordAnalysisClick = useCallback(async () => {
    if (analysisClickRecordedRef.current) return;
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }
      const analyzeGameDate = gameState.gameDate || new Date().toISOString().split('T')[0];
      const resp = await fetch(`${apiBaseUrl}/api/${activeCategory}/analysis/click`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ avatar_id: 'baron-wuffett', game_date: analyzeGameDate }),
      });
      if (resp.ok) {
        analysisClickRecordedRef.current = true;
      }
    } catch {
      // Will retry when dialogue opens
    }
  }, [activeCategory, gameState.gameDate]);

  const handleAnalyze = useCallback(async () => {
    setIsFinishedModalOpen(false);
    setModalDismissed();
    // Small delay so modal exit animation completes before dialogue appears
    setTimeout(() => setShowBaronDialogue(true), 250);

    // Record click (awaited with retry safety)
    recordAnalysisClick();
  }, [setModalDismissed, recordAnalysisClick]);

  const handleDialogueClose = useCallback(() => {
    setShowBaronDialogue(false);
    setProgressRefreshKey(k => k + 1);
    setIsFinishedModalOpen(true);
  }, []);

  const { submitPlay } = useSubmitPlay({ enabled: !devMode });
  const { user } = useAuth();
  const { isPremium } = usePremium();

  // Sync guess history from Supabase for logged-in users (replaces placeholders with real data)
  const handleSyncComplete = useCallback((syncedGuesses: GuessResult[]) => {
    // Prevent cross-day data from leaking into today's game
    const today = getCurrentGameTimestamp();
    if (!isReplayMode && gameState.gameDate !== today) return;

    // Only replace if current guesses are placeholders
    const hasPlaceholders = gameState.guesses.length > 0 &&
      gameState.guesses.every(g => g.company === '(Previous guess)');
    if (!hasPlaceholders) return;

    // Update localStorage
    const storageKey = 'wallstreetle_categories';
    const raw = localStorage.getItem(storageKey);
    const categoryData = raw ? JSON.parse(raw) : {};
    if (categoryData[activeCategory]?.gameState) {
      categoryData[activeCategory].gameState.guesses = syncedGuesses;
      localStorage.setItem(storageKey, JSON.stringify(categoryData));
    }

    // Update React state
    updateGameState({ guesses: syncedGuesses });
  }, [gameState.guesses, gameState.gameDate, activeCategory, updateGameState, isReplayMode]);

  useGuessHistorySync({
    category: activeCategory,
    gameDate: replayDate || gameState.gameDate || getCurrentGameTimestamp(),
    localGuesses: gameState.guesses,
    onSyncComplete: handleSyncComplete,
    isReplayMode
  });

  // Pre-fetch analysis when game completes so there's no lag when dialogue opens
  // Skip for non-premium users on non-popular categories (would return 403)
  const canAccessAnalysis = activeCategory === 'popular' || isPremium;
  const { analysis, isLoading: analysisLoading } = useAnalysis({
    category: activeCategory,
    gameDate: replayDate || gameState.gameDate || getCurrentGameTimestamp(),
    enabled: gameState.isGameComplete && canAccessAnalysis,
  });

  // Memoize the submit handler to prevent effect re-runs
  const handleGameSubmit = useCallback(async () => {
    if (!gameState.isGameComplete || !gameState.timerResult) return;

    const timerResult = gameState.timerResult;
    const gameDate = gameState.gameDate;
    if (!gameDate) return; // Don't submit without a known game date
    const won = gameState.gameStatus === 'won';

    await submitPlay({
      category: currentCategory,
      gameDate,
      guesses: gameState.guesses.length,
      timeMs: timerResult.elapsedMs,
      userId: user?.id || null,
      deviceId: user ? null : getOrCreateDeviceId(),
      won
    });
  }, [
    currentCategory,
    gameState.gameDate,
    gameState.gameStatus,
    gameState.guesses.length,
    gameState.isGameComplete,
    gameState.timerResult,
    submitPlay,
    user
  ]);

  useEffect(() => {
    handleGameSubmit();
  }, [handleGameSubmit]); // Keep callback dependency for correctness

  // Fetch category-specific stock data from backend when bootstrap data is unavailable
  const {
    stockData: hookStockData,
    chartData: hookChartData,
    newsEvents: hookNewsEvents,
    isLoading: hookStockLoading,
  } = useCategoryStockData({
    category: activeCategory,
    autoFetch: !hasBootstrapData
  });

  const bootstrapSteps = bootstrap?.bootstrapState.steps;
  const bootstrapErrors = bootstrap?.bootstrapState.errors;

  const orchestratedStock = bootstrap?.stock;
  const apiStockData = orchestratedStock?.stockData ?? hookStockData;

  // Memoize chartData to prevent unnecessary re-renders of GameMainContent
  const chartData = useMemo(() => {
    return orchestratedStock?.chartData ?? hookChartData;
  }, [orchestratedStock?.chartData, hookChartData]);

  // News events from bootstrap or direct hook
  const newsEvents = orchestratedStock?.newsEvents ?? hookNewsEvents;
  const stockDataLoading = hasBootstrapData
    ? (!orchestratedStock?.stockData && bootstrapSteps?.stock === 'loading')
    : hookStockLoading;

  // Guard against stock data update loops by tracking the last stock ID
  const lastStockIdRef = React.useRef<string | null>(null);

  // Stable boolean for whether solution exists (avoids object ref in deps)
  const hasSolution = Boolean(gameState.solution);

  // Update game state with stock data when it loads (and reset hints if stock changed)
  useEffect(() => {
    if (!apiStockData) return;

    // Skip during category switch — route category differs from context category.
    // switchCategory handles state transition; calling updateStockData here would
    // see a symbol mismatch (old vs new category) and destructively reset guesses.
    if (category && category !== currentCategory) return;

    // Create a stable identifier for the stock data to prevent unnecessary updates
    const stockId = apiStockData.Symbol ?? JSON.stringify(apiStockData).slice(0, 100);

    // Allow re-setting if solution was cleared (e.g., state reload from localStorage)
    if (lastStockIdRef.current === stockId && hasSolution) return;

    lastStockIdRef.current = stockId;

    if (isReplayMode && replayDate) {
      // Set solution and reset game state in a single update to avoid
      // redundant renders (previously reset then updateStockData separately)
      updateGameState({
        solution: apiStockData,
        guesses: [],
        attempts: 4,
        gameStatus: 'playing',
        isGameComplete: false,
        currentRow: 0,
        visibleHints: 1,
        error: null,
        gameDate: replayDate
      });
    } else {
      updateStockData(apiStockData);
    }
  }, [apiStockData, updateStockData, isReplayMode, replayDate, updateGameState, hasSolution, category, currentCategory]);

  // Check if user has already played today (prevents replay after clearing localStorage)
  useEffect(() => {
    if (!hasBootstrapData || !bootstrap?.bootstrapState) return;

    const { hasPlayedToday, playedGameData } = bootstrap.bootstrapState;

    // If already played and localStorage doesn't have the data, restore it from Supabase
    // Skip if sync already populated real guesses (not placeholders)
    const hasRealGuesses = gameState.guesses.length > 0 &&
      gameState.guesses.some(g => g.company !== '(Previous guess)');
    if (hasPlayedToday && playedGameData && gameState.guesses.length === 0 && !hasRealGuesses) {
      // Create placeholder guesses to match the count from Supabase
      const placeholderGuesses = Array(playedGameData.guesses).fill(null).map((_, index) => ({
        company: '(Previous guess)',
        isCorrect: index === playedGameData.guesses - 1 && playedGameData.won,
        timestamp: new Date()
      }));

      const remainingAttempts = Math.max(0, 4 - playedGameData.guesses);

      // Update localStorage directly
      const storageKey = `wallstreetle_categories`;
      const existingStorage = localStorage.getItem(storageKey);
      const categoryData = existingStorage ? JSON.parse(existingStorage) : {};

      const currentGameDate = replayDate || getCurrentGameTimestamp();

      categoryData[activeCategory] = {
        gameState: {
          ...gameState,
          gameStatus: playedGameData.won ? 'won' : 'lost',
          isGameComplete: true,
          attempts: remainingAttempts,
          guesses: placeholderGuesses,
          visibleHints: 4,
          gameDate: currentGameDate,
          timerResult: {
            elapsedMs: playedGameData.timeMs,
            wasStarted: true
          }
        },
        lastSaved: new Date().toISOString(),
        version: 1
      };

      localStorage.setItem(storageKey, JSON.stringify(categoryData));

      // Also update React state
      updateGameState({
        gameStatus: playedGameData.won ? 'won' : 'lost',
        isGameComplete: true,
        attempts: remainingAttempts,
        guesses: placeholderGuesses,
        visibleHints: 4,
        gameDate: currentGameDate,
        timerResult: {
          elapsedMs: playedGameData.timeMs,
          wasStarted: true
        }
      });
    }
  }, [hasBootstrapData, bootstrap?.bootstrapState, gameState.guesses, gameState, activeCategory, updateGameState, replayDate]);

  // Use API stock data if available, otherwise fall back to game state
  // Memoize stockData to prevent unnecessary re-renders of GameMainContent
  const stockData = useMemo(() => {
    return apiStockData || gameState.solution;
  }, [apiStockData, gameState.solution]);

  // Preload company logo so it's cached before Baron Wuffett overlay opens
  useEffect(() => {
    const logoUrl = stockData?.logo?.url;
    if (logoUrl) {
      const img = new Image();
      img.src = logoUrl;
    }
  }, [stockData?.logo?.url]);

  const {
    metrics: hookMetrics,
    isLoading: hookMetricsLoading,
    error: hookMetricsError,
    selectedGroups: hookSelectedGroups,
  } = useFinancialMetrics({
    category: activeCategory,
    autoFetch: !hasBootstrapData,
    date: replayDate
  });

  const orchestratedMetrics = bootstrap?.metrics;

  // Memoize effectiveMetrics to prevent unnecessary re-renders of GameMainContent
  const effectiveMetrics = useMemo(() => {
    return orchestratedMetrics?.metrics ?? hookMetrics;
  }, [orchestratedMetrics?.metrics, hookMetrics]);

  // Resolve selectedGroups from bootstrap or hook
  const effectiveSelectedGroups = useMemo(() => {
    return orchestratedMetrics?.selectedGroups ?? hookSelectedGroups ?? null;
  }, [orchestratedMetrics?.selectedGroups, hookSelectedGroups]);

  const metricsLoading = hasBootstrapData
    ? (!orchestratedMetrics?.hasLiveData && bootstrapSteps?.metrics === 'loading')
    : hookMetricsLoading;
  const metricsError = hasBootstrapData ? bootstrapErrors?.metrics ?? null : hookMetricsError;

  // Derived booleans for desktop inline rendering of financial metrics
  const isWallstreetbetsCategory = activeCategory === 'wallstreetbets';
  const showPrice = gameState.visibleHints >= 2;
  const showDate = true;
  // News bubbles: 1 at hint 2, 2 at hint 3, 3 at hint 4 (top N by absolute magnitude)
  const visibleNewsCount =
    gameState.visibleHints >= 4 ? 3
    : gameState.visibleHints >= 3 ? 2
    : gameState.visibleHints >= 2 ? 1
    : 0;

  const hasMetricsValues = useMemo(() => {
    return Boolean(
      effectiveMetrics &&
      Object.values(effectiveMetrics).some(value => value !== null)
    );
  }, [effectiveMetrics]);

  const shouldShowMetricsError = useMemo(() => {
    return Boolean(
      metricsError &&
      !metricsLoading &&
      !hasMetricsValues
    );
  }, [metricsError, metricsLoading, hasMetricsValues]);

  const showMetricsLoading = useMemo(() => {
    return metricsLoading && !hasMetricsValues;
  }, [metricsLoading, hasMetricsValues]);

  // Memoize metricsMap as a flat Record<string, string | null> for FinancialMetrics
  const emptyMetricsMap = useMemo<Record<string, string | null>>(() => ({}), []);

  const metricsForRender = useMemo<Record<string, string | null>>(() => {
    if (!effectiveMetrics) return emptyMetricsMap;
    return effectiveMetrics as unknown as Record<string, string | null>;
  }, [effectiveMetrics, emptyMetricsMap]);

  // Fetch hints from the remote hints service (category-specific if category is provided)
  const {
    hints: hookHints,
    isLoading: hookHintsLoading,
    error: hookHintsError
  } = useHints({
    autoFetch: !hasBootstrapData,
    category: activeCategory,
    date: replayDate
  });

  const orchestratedHints = bootstrap?.hints;
  const effectiveHints = orchestratedHints?.hints ?? hookHints;
  const hintsLoading = hasBootstrapData
    ? (!orchestratedHints?.hasLiveData && bootstrapSteps?.hints === 'loading')
    : hookHintsLoading;
  const hintsError = hasBootstrapData ? bootstrapErrors?.hints ?? null : hookHintsError;

  // Process hints from API and apply visibility logic
  const processedHints = useMemo((): HintData[] => {
    if (!effectiveHints.length) return [];

    return effectiveHints.map((hint, index) => ({
      ...hint,
      isVisible: index < gameState.visibleHints,
    }));
  }, [effectiveHints, gameState.visibleHints]);



  // Handle all game effects and synchronization
  useGameEffects({
    category: activeCategory,
    categoryConfig,
    currentCategory,
    gameState,
    apiStockData,
    isStockLoading: stockDataLoading,
    isSubmitting,
    updateGameState,
    setGameResult,
    generateGameResult
  });

  // Memoize keyboard handler callbacks to prevent recreation on every render
  const onEnter = useCallback(() => {
    handleGuessSubmit(gameState.currentGuess);
    updateGameState({ currentGuess: '' });
  }, [gameState.currentGuess, handleGuessSubmit, updateGameState]);

  const onDelete = useCallback(() => {
    // Delete functionality is handled within the hook
  }, []);

  const triggerShakeStable = useCallback(() => {
    // This will be handled in the useGameLogic hook
  }, []);

  // Handle keyboard interactions (not currently used but may be needed later)
  useKeyboardHandler({
    gameState,
    isSubmitting,
    updateGameState,
    triggerHapticFeedback, // Pass stable reference directly, no wrapper
    triggerShake: triggerShakeStable,
    onEnter,
    onDelete
  });

  // Consume the ref value when modal opens so it's only true once
  const autoOpenProgress = isFinishedModalOpen && isFirstFinishedOpenRef.current;

  useEffect(() => {
    if (isFinishedModalOpen && isFirstFinishedOpenRef.current) {
      isFirstFinishedOpenRef.current = false;
    }
  }, [isFinishedModalOpen]);

  // Memoize finished modal to prevent recreation on every render
  const finishedModal = useMemo(() => (
    <FinishedModal
      isOpen={isFinishedModalOpen}
      onClose={handleModalClose}
      gameState={gameState}
      onShare={handleShare}
      category={currentCategory}
      isReplayMode={isReplayMode}
      onAnalyze={handleAnalyze}
      progressRefreshKey={progressRefreshKey}
      autoOpenProgress={autoOpenProgress}
    />
  ), [isFinishedModalOpen, handleModalClose, gameState, handleShare, currentCategory, isReplayMode, handleAnalyze, progressRefreshKey, autoOpenProgress]);

  // Show loading state
  if (gameState.isLoading) {
    return <GameLoading />;
  }

  // Show error state
  if (gameState.error && !gameState.solution) {
    return <GameError error={gameState.error} onRetry={memoizedHandleRetry} />;
  }

  // Render desktop dashboard layout
  if (layoutVariant.type === 'desktop') {
    return (
      <GameErrorBoundary onGameReset={handleErrorBoundaryReset}>
        <div className={cn('bg-background', className)}>
          {/* Ultra-wide constraint wrapper */}
          <div className="max-w-[1920px] mx-auto w-full">
            {/* Desktop Dashboard Layout */}
            <div className="relative overflow-hidden">
            <GuessFeedbackOverlay type={feedbackType} />
            <DesktopGameLayout
              chartContent={
                <>
                  {/* Header Section - Above Chart */}
                  <GameHeader
                    category={activeCategory}
                    currentCategory={currentCategory}
                    stockData={stockData}
                    error={gameState.error}
                    layout="desktop"
                  />

                  {/* Price Chart */}
                  <div className="relative" data-spotlight-id="price-chart">
                    {isWallstreetbetsCategory && <WsbSlotMachineBackground />}
                    <StockChart
                      data={chartData}
                      loading={stockDataLoading}
                      backgroundClassName={isWallstreetbetsCategory ? 'relative isolate' : undefined}
                      showPrice={showPrice}
                      showDate={showDate}
                      category={activeCategory}
                      isReplayMode={isReplayMode}
                      newsEvents={newsEvents}
                      visibleNewsCount={visibleNewsCount}
                    />
                  </div>
                </>
              }
              cluesContent={
                <>
                  {/* Financial Metrics */}
                  <section ref={financialMetricsRef} data-tutorial-id="financial-metrics">
                    {shouldShowMetricsError ? (
                      <div className="space-y-4">
                        <FinancialMetrics
                          metricsMap={emptyMetricsMap}
                          selectedGroups={effectiveSelectedGroups}
                          loading={false}
                          layout="sidebar"
                          visibleHints={gameState.visibleHints}
                          category={activeCategory}
                          spotlightStep={spotlightStep}
                        />
                        <div className="text-sm text-muted-foreground">
                          {metricsError}
                        </div>
                      </div>
                    ) : (
                      <FinancialMetrics
                        metricsMap={metricsForRender}
                        selectedGroups={effectiveSelectedGroups}
                        loading={showMetricsLoading}
                        layout="sidebar"
                        visibleHints={gameState.visibleHints}
                        category={activeCategory}
                        spotlightStep={spotlightStep}
                      />
                    )}
                  </section>

                  {/* Company Hints */}
                  <section data-tutorial-id="hints-section">
                    <GameFooter
                      processedHints={processedHints}
                      visibleHints={gameState.visibleHints}
                      stockData={gameState.solution || null}
                      currentCategory={currentCategory}
                      hintsLoading={hintsLoading}
                      hintsError={hintsError}
                    />
                  </section>
                </>
              }
              controlsContent={
                <section data-tutorial-id="submit-guesses" className="space-y-4 flex flex-col flex-1">
                  <GameControls
                    gameState={gameState}
                    currentCategory={currentCategory}
                    category={activeCategory}
                    isSubmitting={isSubmitting}
                    isShaking={isShaking}
                    stockOptions={stockOptions}
                    stockOptionsLoading={stockOptionsLoading}
                    stockOptionsError={stockOptionsError}
                    searchStockOptions={searchStockOptions}
                    onGuessSubmit={handleGuessSubmit}
                    onShowResults={handleOpenResults}
                    onInvalidGuess={handleInvalidGuess}
                    validationError={validationError}
                    onClearValidationError={handleClearValidationError}
                    onCopyResults={handleShare}
                    layout="desktop-sidebar"
                    financialMetricsRef={financialMetricsRef}
                  />
                </section>
              }
            />
            </div>

          {/* Finished Modal */}
          {finishedModal}

          {/* Baron Wuffett Dialogue */}
          <BaronWuffettDialogue
            isOpen={showBaronDialogue}
            onClose={handleDialogueClose}
            onStepChange={setSpotlightStep}
            analysis={analysis}
            isAnalysisLoading={analysisLoading}
            category={activeCategory}
            selectedGroups={effectiveSelectedGroups}
            onDialogueOpen={recordAnalysisClick}
            gameDate={replayDate || gameState.gameDate || getCurrentGameTimestamp()}
            logoUrl={stockData?.logo?.url}
          />
          {spotlightStep && <AnalysisSpotlight step={spotlightStep} />}

          {/* Tutorial Overlay (Desktop Only) */}
          <TutorialOverlay />

          {/* Dev Reset Button (Development Only) */}
          {devResetCategory && (
            <DevResetButton
              category={activeCategory}
              onReset={devResetCategory}
            />
          )}
          </div>
        </div>
      </GameErrorBoundary>
    );
  }

  // Render mobile layout
  return (
    <GameErrorBoundary onGameReset={handleErrorBoundaryReset}>
      <div className={cn('bg-background', className)}>
        {/* Ultra-wide constraint wrapper */}
        <div
          className={cn(
            'mx-auto w-full',
            shouldCenterStreamerLayout ? 'max-w-[675px]' : 'max-w-[1920px]'
          )}
        >
          <div className="relative overflow-hidden">
          <GuessFeedbackOverlay type={feedbackType} fullScreen={shouldCenterStreamerLayout} />
          <MobileGameLayout
            header={gameState.error ? (
              <GameHeader
                category={activeCategory}
                currentCategory={currentCategory}
                stockData={stockData}
                error={gameState.error}
                layout="mobile"
              />
            ) : undefined}
            mainContent={
              <GameMainContent
                stockData={stockData}
                chartData={chartData}
                stockDataLoading={stockDataLoading}
                category={activeCategory}
                currentCategory={currentCategory}
                financialMetrics={metricsForRender}
                selectedGroups={effectiveSelectedGroups}
                financialMetricsLoading={metricsLoading}
                financialMetricsError={metricsError}
                visibleHints={gameState.visibleHints}
                metricsRef={financialMetricsRef}
                spotlightStep={spotlightStep}
                isReplayMode={isReplayMode}
                isStreamerMode={shouldCenterStreamerLayout}
                newsEvents={newsEvents}
              />
            }
            hints={
              <GameFooter
                processedHints={processedHints}
                visibleHints={gameState.visibleHints}
                stockData={gameState.solution || null}
                currentCategory={currentCategory}
                hintsLoading={hintsLoading}
                hintsError={hintsError}
                isStreamerMode={shouldCenterStreamerLayout}
              />
            }
            controls={
              <GameControls
                gameState={gameState}
                currentCategory={currentCategory}
                category={activeCategory}
                isSubmitting={isSubmitting}
                isShaking={isShaking}
                stockOptions={stockOptions}
                stockOptionsLoading={stockOptionsLoading}
                stockOptionsError={stockOptionsError}
                searchStockOptions={searchStockOptions}
                onGuessSubmit={handleGuessSubmit}
                onShowResults={handleOpenResults}
                onInvalidGuess={handleInvalidGuess}
                validationError={validationError}
                onClearValidationError={handleClearValidationError}
                onCopyResults={handleShare}
                layout="mobile-centered"
                financialMetricsRef={financialMetricsRef}
              />
            }
          />
          </div>

        {/* Finished Modal */}
        {finishedModal}

        {/* Baron Wuffett Dialogue */}
        <BaronWuffettDialogue
          isOpen={showBaronDialogue}
          onClose={handleDialogueClose}
          onStepChange={setSpotlightStep}
          analysis={analysis}
          isAnalysisLoading={analysisLoading}
          category={activeCategory}
          selectedGroups={effectiveSelectedGroups}
          gameDate={replayDate || gameState.gameDate || getCurrentGameTimestamp()}
          logoUrl={stockData?.logo?.url}
        />
        {spotlightStep && <AnalysisSpotlight step={spotlightStep} />}

        {/* Tutorial Overlay (Mobile Only) */}
        <MobileTutorialOverlay />

        {/* Dev Reset Button (Development Only) */}
        {devResetCategory && (
          <DevResetButton
            category={activeCategory}
            onReset={devResetCategory}
          />
        )}
        </div>
      </div>
    </GameErrorBoundary>
  );
};

export default GamePage;
